import { ErrorOutline } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import qs from "qs";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import {
  BAAS_STATEMENT_PAGE_SIZE,
  useAccountBalanceData,
  useAccountData,
  useTransactionsData,
} from "../../../Hooks";
import { ScreenNotification } from "../../../Components/Atoms";
import ModalPin from "../Operators/ModalPin";
import { isDevelopmentOnly } from "../../../Utils/developmentFeature";
import {
  DashboardSkeleton,
  HomeActiveAccountBlock,
  ScreenGreeting,
  TransactionListSkeleton,
} from "../../../Components/Molecules";
import {
  getItemsDateRangeLabel,
  GridPagination,
} from "../../../Components/Molecules/GridHistory";
import { useHomeIndividualAccount } from "../../../Components/Molecules/HomeAccountSelector";
import { IndividualAccountPromoBanner } from "../../../Components/Molecules/IndividualAccountPromoBanner";
import { IndividualAccountOnboardingModal } from "../../../Components/Molecules/IndividualAccountOnboardingModal";
import { TwoFactorPromoBanner } from "../../../Components/Molecules/TwoFactorPromoBanner";
import { TwoFactorSettingsModal } from "../../../Components/Molecules/TwoFactorSettingsModal";
import { useFidoPilotOnboarding } from "../../../Hooks/useFidoPilotOnboarding";
import { HomeBalanceCards } from "../../../Components/Molecules/HomeBalanceCards";
import { HomeQuickActions } from "../../../Components/Molecules/HomeQuickActions";
import { PixCardsDashboard } from "../../../Components/Molecules/PixCardsDashboard";
import { useAuth } from "../../../Contexts/AuthContext";
import {
  getStoredSelectedAccountId,
  IB_ACTIVE_ACCOUNT_CHANGED_EVENT,
  isLegacyAccountTypeId,
} from "../../../Constants/accountTypes";
import { useActiveIbAccount } from "../../../Hooks/useActiveIbAccount";
import {
  balancesFromMeAccount,
  mergeNormalizedAccountBalances,
  resolveMeAccount,
} from "../../../Hooks/Normalizers";
import { useFilter } from "../../../Hooks/useFilter";
import { resolveIbAccountEntityName } from "../../../Services/operatingAccountsApi";
import {
  formatGreetingDisplayName,
  resolveDashboardGreetingKey,
} from "../../../Utils/timeGreeting";
import { ExtractDayTimeline } from "../Extract/ExtractDayTimeline";
import { ExtractFilterBar } from "../Extract/ExtractFilterBar";
import { ExtractPeriodSummary } from "../Extract/ExtractPeriodSummary";
import { ExtractTimelineToggleAll } from "../Extract/ExtractTimelineToggleAll";
import {
  applyExtractClientFilters,
  estimateExtractTotal,
  extractFilterObjFromFilters,
  filtersFromExtractFilterObj,
  flattenTransactionDayGroups,
  regroupTransactionsByDay,
} from "../Extract/extractFilters";
import { InternalTransferModal } from "../Transfers/InternalTransferModal";
import { PixQrReceiveModal } from "../Pix/PixSales/PixQrReceiveModal";

const HOME_PATH = "/";
const DEFAULT_ROWS_PER_PAGE = 10;
const EMPTY_SUB_ACCOUNTS = [];

export const Dashboard = () => {
  const history = useHistory();
  const location = useLocation();
  const parsedQS = qs.parse(location.search.replace("?", ""));
  const [filters, setFilters] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [allExpanded, setAllExpanded] = useState(true);
  const [transferModal, setTransferModal] = useState(false);
  const [qrReceiveModal, setQrReceiveModal] = useState(false);
  const { hasPermission } = useAuth();
  const canTransfer = hasPermission("transp2p");
  const canReceivePix = hasPermission("receivepix");
  const {
    state: accountData,
    handleGET: fetchAccountGET,
    loading: accountLoading,
  } = useAccountData();
  const {
    state: accountBalanceData,
    handleGET: fetchAccountBalanceGET,
    balanceValue,
    loading: balanceLoading,
    accountId: balanceAccountId,
  } = useAccountBalanceData();
  const {
    handleGET: fetchTransactionsGET,
    loading: transactionsLoading,
    state: transactionsData,
  } = useTransactionsData();
  const individualAccount = useHomeIndividualAccount(accountData);
  const fidoPilot = useFidoPilotOnboarding(accountData);
  const { account: activeIbAccount } = useActiveIbAccount();
  const { getFilterObj, handleRemoveFilters } = useFilter();
  const homeFilterObj = getFilterObj("homeStatement");

  // Greeting: time-based salutation + active account / entity display name.
  const welcomeAccountName = useMemo(() => {
    if (!activeIbAccount) return "";
    const raw = String(
      activeIbAccount.entityName
        || activeIbAccount.pickerEntityName
        || resolveIbAccountEntityName(activeIbAccount)
        || activeIbAccount.label
        || "",
    ).trim();
    return formatGreetingDisplayName(raw);
  }, [activeIbAccount]);

  const greetingTitle = useMemo(() => {
    if (!welcomeAccountName) {
      return intl.get("dashboard.welcome") || "Bem-vindo";
    }
    const key = resolveDashboardGreetingKey();
    return (
      intl.get(key, { name: welcomeAccountName })
      || `Boa noite, ${welcomeAccountName}`
    );
  }, [welcomeAccountName]);

  const homeBalanceData = useMemo(() => {
    const selectedId = String(activeIbAccount?.id || getStoredSelectedAccountId() || "").trim();
    const fromMe = balancesFromMeAccount(
      resolveMeAccount(accountData?.accounts, selectedId),
    );
    // Always merge GET + /me. Returning only fromMe when account_id was missing
    // dropped Over Balance (limitCents / showLimit) from GET /balances.
    return mergeNormalizedAccountBalances(accountBalanceData, fromMe)
      || accountBalanceData
      || fromMe;
  }, [activeIbAccount, accountData?.accounts, accountBalanceData, balanceAccountId]);

  const hasApiFilters = Boolean(
    homeFilterObj.start_date
    || homeFilterObj.end_date
    || homeFilterObj.start_amount
    || homeFilterObj.end_amount
    || homeFilterObj.sub_account_id,
  );

  const buildStatementQuery = useCallback(
    () =>
      hasApiFilters
        ? {
            page,
            // BaaS max page_size = 100 (GridPagination options stay ≤ 50).
            page_size: Math.min(BAAS_STATEMENT_PAGE_SIZE, Math.max(1, rowsPerPage)),
            ...homeFilterObj,
          }
        : {
            limit: 5,
            ...homeFilterObj,
          },
    [hasApiFilters, page, rowsPerPage, homeFilterObj],
  );

  const homeApiFilterKey = [
    homeFilterObj.start_date,
    homeFilterObj.end_date,
    homeFilterObj.start_amount,
    homeFilterObj.end_amount,
    homeFilterObj.sub_account_id,
    page,
    rowsPerPage,
    hasApiFilters,
  ];

  const refreshHomeForActiveAccount = useCallback(() => {
    // Re-fetch accountSettings after switch — account_code / features follow the active
    // account. Session name / e-mail / CPF stay the logged-in AccountPerson.
    fetchAccountGET();
    fetchAccountBalanceGET({ account_id: getStoredSelectedAccountId() });
    fetchTransactionsGET(buildStatementQuery());
    // handleGET from useCallAxios is not memoized; keep stable on filter/page changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, homeApiFilterKey);

  useEffect(() => {
    const onActiveAccountChanged = () => {
      refreshHomeForActiveAccount();
    };
    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onActiveAccountChanged);
    return () => {
      window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onActiveAccountChanged);
    };
  }, [refreshHomeForActiveAccount]);

  useEffect(() => {
    fetchAccountGET();
    const stored = getStoredSelectedAccountId();
    if (stored && !isLegacyAccountTypeId(stored)) {
      fetchAccountBalanceGET({ account_id: stored });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const accounts = accountData?.accounts;
    if (!Array.isArray(accounts) || accounts.length === 0) return;
    const selectedId = String(activeIbAccount?.id || getStoredSelectedAccountId() || "").trim();
    const matched = resolveMeAccount(accounts, selectedId);
    const id = String(matched?.id || matched?.account_id || "").trim();
    if (!id || isLegacyAccountTypeId(id)) return;
    fetchAccountBalanceGET({ account_id: id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData?.accounts, activeIbAccount?.id]);

  useEffect(() => {
    const openTransfer =
      String(parsedQS.transfer) === "1"
      || String(parsedQS.newTransfer) === "true";
    if (openTransfer && canTransfer) {
      setTransferModal(true);
      history.replace(HOME_PATH);
    } else if (String(parsedQS.qrReceive) === "true" && canReceivePix) {
      setQrReceiveModal(true);
      history.replace(HOME_PATH);
    }
    // Only react to the landing URL (deep link / quick action query).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilters((prev) => {
      const fromApi = filtersFromExtractFilterObj(homeFilterObj);
      return { ...fromApi, types: prev?.types ?? [], searchQuery: prev?.searchQuery ?? "" };
    });
  }, [
    homeFilterObj.start_date,
    homeFilterObj.end_date,
    homeFilterObj.start_amount,
    homeFilterObj.end_amount,
    homeFilterObj.sub_account_id,
  ]);

  useEffect(() => {
    fetchTransactionsGET(buildStatementQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeFilterObj, page, rowsPerPage, hasApiFilters]);

  const flatItems = useMemo(
    () => flattenTransactionDayGroups(transactionsData?.items),
    [transactionsData?.items],
  );

  const filteredFlatItems = useMemo(
    () => applyExtractClientFilters(flatItems, filters),
    [flatItems, filters],
  );

  const displayGroups = useMemo(
    () => regroupTransactionsByDay(filteredFlatItems),
    [filteredFlatItems],
  );

  const timelineDayKey = useMemo(
    () =>
      displayGroups
        .map((group) => (group ? Object.keys(group)[0] : null))
        .filter(Boolean)
        .join("|"),
    [displayGroups],
  );

  useEffect(() => {
    setAllExpanded(true);
  }, [timelineDayKey, page]);

  const totalEstimate = estimateExtractTotal(
    page,
    transactionsData?.pageCount,
    rowsPerPage,
    filteredFlatItems.length,
  );

  // Only show "Mais recentes" when no date range (filter bar owns the chrome when dates are set).
  const periodLabel = useMemo(() => {
    const hasDateFilter = Boolean(filters?.dateFrom || filters?.dateTo);
    if (hasDateFilter) return null;

    const derivedRange = getItemsDateRangeLabel(filteredFlatItems);
    if (derivedRange) {
      return intl.get("extract.periodMostRecentWithRange", { range: derivedRange });
    }
    return intl.get("extract.periodMostRecent");
  }, [filters?.dateFrom, filters?.dateTo, filteredFlatItems]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
    handleRemoveFilters("homeStatement", extractFilterObjFromFilters(nextFilters));
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  // Only full-page skeleton on first bootstrap. Refetch (e.g. active-account event) must
  // keep the home shell mounted — swapping to skeleton used to unmount the transfer modal.
  const hasBootstrappedAccount = Boolean(accountData?.userName || accountData?.userId);
  const isLoading =
    filters === null
    || ((accountLoading.GET || balanceLoading.GET) && !hasBootstrappedAccount);

  const topNotification = "";

  return (
    <div className="dashboard-page w-full min-w-0 max-w-full">
      {isDevelopmentOnly && <ModalPin />}

      {isLoading ? (
        <DashboardSkeleton />
      ) : (
        <>
          {!!topNotification && (
            <ScreenNotification
              icon={<ErrorOutline style={{ marginRight: 10 }} />}
              text={topNotification}
            />
          )}

          <IndividualAccountOnboardingModal
            open={individualAccount.onboardingOpen}
            onClose={individualAccount.closeOnboarding}
            accountData={accountData}
            onSubmit={individualAccount.submitOnboarding}
            submitting={individualAccount.submitting}
            onSuccess={individualAccount.refreshStatus}
          />

          <TwoFactorSettingsModal
            open={fidoPilot.modalOpen}
            onClose={fidoPilot.closeModal}
            settings={fidoPilot.settings}
            registerFidoKey={fidoPilot.registerFidoKey}
            accountKey={fidoPilot.accountKey}
            removeFidoKey={fidoPilot.removeFidoKey}
            setPreferredLogin={fidoPilot.setPreferredLogin}
            setSelectedLoginMethods={fidoPilot.setSelectedLoginMethods}
            setPersonPhone={fidoPilot.setPersonPhone}
            configureGoogleAuthenticator={fidoPilot.configureGoogleAuthenticator}
            removeGoogleAuthenticator={fidoPilot.removeGoogleAuthenticator}
          />

          {fidoPilot.showBanner ? (
            <TwoFactorPromoBanner onOpenSettings={fidoPilot.openModal} />
          ) : null}

          <IndividualAccountPromoBanner
            canUseIndividualAccount={individualAccount.canUseIndividualAccount}
            isPendingReview={individualAccount.isPendingReview}
            needsOnboarding={individualAccount.needsOnboarding}
            isLoadingStatus={individualAccount.isLoadingStatus}
            isKycPending={individualAccount.isKycPending}
            personStatus={individualAccount.personStatus}
            onOpenOnboarding={individualAccount.openOnboarding}
          />

          <ScreenGreeting title={greetingTitle}>
            <HomeActiveAccountBlock account={activeIbAccount} />
          </ScreenGreeting>

          <HomeBalanceCards
            key={activeIbAccount?.id || "ib-home-balances"}
            accountBalanceData={homeBalanceData}
            balanceValue={balanceValue}
            loadingRefresh={balanceLoading}
            onRefresh={() =>
              fetchAccountBalanceGET({
                account_id: activeIbAccount?.id || getStoredSelectedAccountId(),
              })
            }
          />

          <HomeQuickActions
            onOpenTransfer={canTransfer ? () => setTransferModal(true) : undefined}
            onOpenQrReceive={canReceivePix ? () => setQrReceiveModal(true) : undefined}
          />

          <PixCardsDashboard />

          <section className="mb-5">
            <div className="mb-3">
              <h2 className="m-0 text-sm font-bold text-brand-dark dark:text-gray-100">
                {intl.get("phrases.lastTransactions")}
              </h2>
              {periodLabel ? (
                <p className="mt-1 m-0 text-xs text-gray-500 dark:text-gray-400">
                  {periodLabel}
                </p>
              ) : null}
            </div>

            <div className="mb-4">
              <ExtractFilterBar
                filters={filters}
                onFiltersChange={handleFiltersChange}
                filteredItems={filteredFlatItems}
                subAccounts={EMPTY_SUB_ACCOUNTS}
                leadingActions={
                  hasPermission("statementsview") ? (
                    <RouterLink
                      to="/extrato"
                      className="grid-history-action-btn grid-history-action-btn--primary no-underline"
                    >
                      {intl.get("common.seeMore")}
                    </RouterLink>
                  ) : null
                }
              />
              <ExtractPeriodSummary items={filteredFlatItems} className="mt-4" />
            </div>

            <div className="w-full">
              {transactionsLoading.GET ? (
                <TransactionListSkeleton rows={hasApiFilters ? 8 : 4} />
              ) : (
                <>
                  {displayGroups.length > 0 && (
                    <ExtractTimelineToggleAll
                      allExpanded={allExpanded}
                      onToggle={() => setAllExpanded((prev) => !prev)}
                    />
                  )}
                  <ExtractDayTimeline
                    dayGroups={displayGroups}
                    allExpanded={allExpanded}
                    onAllExpandedChange={setAllExpanded}
                    onRefundSuccess={refreshHomeForActiveAccount}
                  />
                  {hasApiFilters && (
                    <div className="mt-4">
                      <GridPagination
                        total={totalEstimate}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        currentPageCount={filteredFlatItems.length}
                        onPageChange={setPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        itemLabel={intl.get("common.transactions")}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </>
      )}

      {canTransfer ? (
        <InternalTransferModal
          open={transferModal}
          onClose={() => setTransferModal(false)}
        />
      ) : null}
      {canReceivePix ? (
        <PixQrReceiveModal
          open={qrReceiveModal}
          onClose={() => setQrReceiveModal(false)}
        />
      ) : null}
    </div>
  );
};
